import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ChangelogService } from '../../services/changelog.service';
import { ImageLightbox, LightboxImage } from '../image-lightbox/image-lightbox';
import type { ChangelogFeedScreenshot } from '../../domainTypes/changelog';
import type { WaHideEvent } from '@web.awesome.me/webawesome-pro';

/** "What's new" feed: renders every published changelog entry newest-first,
 *  badging entries newer than the user's Seen watermark. Self-gating like
 *  TcAcceptanceDialog — mounted at app-root, fetches once per authenticated
 *  non-guest login. Auto-opens only when something is new; the account-menu
 *  "What's new?" item opens it on demand via ChangelogService.open(). Any
 *  dismissal acks (advances the watermark only when something was new). */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ImageLightbox],
    selector: 'app-changelog-dialog',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    styleUrl: './changelog-dialog.css',
    templateUrl: './changelog-dialog.html',
})
export class ChangelogDialog {
    private readonly auth = inject(AuthService);
    private readonly changelogService = inject(ChangelogService);

    readonly entries = this.changelogService.entries;
    readonly loadFailed = this.changelogService.loadFailed;
    // Auth-gated so the dialog self-closes on logout instead of lingering over
    // the login page. Opens automatically when at least one entry is new, or on
    // demand when the user hits "What's new?" (openRequested).
    // Auto-open only for a RETURNING user with genuinely-new entries (hasNew but
    // not the whole feed). A brand-new user (allNew = no watermark) is baselined
    // silently instead of walled. On-demand "What's new?" (openRequested) always
    // opens.
    readonly isOpen = computed(
        () =>
            this.auth.state().status === 'authenticated' &&
            ((this.changelogService.hasNew() && !this.changelogService.allNew()) || this.changelogService.openRequested())
    );

    /** Screenshot currently expanded fullscreen (null = lightbox closed). */
    readonly lightboxImage = signal<LightboxImage | null>(null);

    private lastFetchedUserId: number | null = null;

    // Fetch once per authenticated non-guest user. Logout re-arms the guard and
    // clears any stale feed, so a same-user re-login (SPA navigation, no reload)
    // re-fetches and can pick up newly published changelog entries.
    // noinspection JSUnusedLocalSymbols
    private readonly fetchOnLoginEffect = effect(() => {
        const state = this.auth.state();
        if (state.status !== 'authenticated' || !state.user || state.user.status === 'guest' || !state.user.tc_accepted) {
            if (state.status !== 'authenticated') {
                // The lightbox is a SIBLING of the auth-gated wa-dialog, so it
                // is not closed by the dialog's own gating - an expanded
                // internal screenshot stayed fullscreen over the login page
                // after logout/session expiry, since logout is an SPA
                // navigation, not a reload (review-10 #18). Reset outside the
                // lastFetchedUserId guard so a second de-auth still clears it.
                this.lightboxImage.set(null);
                if (this.lastFetchedUserId !== null) {
                    this.lastFetchedUserId = null;
                    this.changelogService.reset();
                }
            }
            return;
        }
        if (this.lastFetchedUserId === state.user.user_id) {
            return;
        }
        this.lastFetchedUserId = state.user.user_id;
        this.changelogService.fetch().subscribe(() => this.baselineIfBrandNew());
    });

    /** Brand-new user (no Seen watermark → the whole feed reads as new): silently
     *  advance their watermark so the changelog never auto-opens on first login.
     *  It stays reachable via the account-menu "What's new?" item, and
     *  genuinely-new future entries auto-open normally. */
    private baselineIfBrandNew(): void {
        if (this.changelogService.allNew() && !this.changelogService.openRequested()) {
            this.changelogService.markSeen().subscribe();
        }
    }

    onHide(e: WaHideEvent) {
        // Ignore wa-hide bubbling from nested elements (e.g. carousel controls).
        if ((e.target as HTMLElement | null)?.tagName?.toLowerCase() !== 'wa-dialog') {
            return;
        }
        // Any dismissal (Escape, light dismiss, close button) acks the feed.
        this.changelogService.markSeen().subscribe();
    }

    onDismiss() {
        this.changelogService.markSeen().subscribe();
    }

    openLightbox(shot: ChangelogFeedScreenshot) {
        this.lightboxImage.set({ url: shot.url, caption: shot.caption });
    }

    closeLightbox() {
        this.lightboxImage.set(null);
    }
}
